import OpenAI from 'openai';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

// Model configuration
const PRIMARY_MODEL = 'gpt-4o-mini'; // Add this line
const INDEXING_MODEL = 'gpt-4o-mini'; // For reading and indexing content
const RESPONSE_MODEL = 'gpt-4o'; // For generating final response
const FALLBACK_MODEL = 'gpt-4o-mini'; // Fallback if gpt-4o fails

// ✅ NEW: Token limits configuration (2-4k range as specified)
const TOKEN_LIMITS = {
  MIN_TOKENS: 2000,
  MAX_TOKENS: 4000,
  DEFAULT_TOKENS: 3000 // Conservative default within range
};

// Custom Assistant instruction as specified by user
const ASSISTANT_INSTRUCTION = `Resuma por capítulos/tópicos com pontos-chave; linguagem direta; não mencionar provedores.

REGRAS OBRIGATÓRIAS:
1. Organize o conteúdo em capítulos ou tópicos claros
2. Para cada seção, destaque os pontos-chave mais importantes
3. Use linguagem direta, objetiva e profissional
4. NUNCA mencione o nome de provedores de IA, serviços ou modelos
5. Mantenha o foco no conteúdo, não na fonte
6. Use formatação Markdown para clareza (##, -, **)

CONSOLIDAÇÃO DE MÚLTIPLOS ARQUIVOS:
- Quando analisar vários arquivos, organize por capítulos/documentos
- Identifique temas comuns entre os arquivos
- Forneça um resumo consolidado estruturado por tópicos principais`;

// Store assistant IDs and thread IDs for reuse
const assistantCache = {
  primary: null,   // GPT-4o-mini assistant
  fallback: null   // GPT-4o assistant
};

const threadCache = new Map(); // conversationId -> threadId mapping

/**
 * Create or retrieve an OpenAI Assistant with specified model
 * @param {string} model - Model to use ('gpt-4o-mini' or 'gpt-4o')
 * @param {boolean} forceCreate - Force creation of new assistant
 * @returns {Promise<string>} Assistant ID
 */
async function getOrCreateAssistant(model = PRIMARY_MODEL, forceCreate = false) {
  const cacheKey = model === PRIMARY_MODEL ? 'primary' : 'fallback';
  
  // Return cached assistant if available and not forcing creation
  if (!forceCreate && assistantCache?.[cacheKey]) {
    console.log(`✅ Using cached assistant (${model}): ${assistantCache?.[cacheKey]}`);
    return assistantCache?.[cacheKey];
  }

  try {
    // Create new assistant
    const assistant = await openai?.beta?.assistants?.create({
      name: `Document Analyzer - ${model}`,
      instructions: ASSISTANT_INSTRUCTION,
      model: model,
      tools: [{ type: 'file_search' }], // Enable file search for document analysis
    });

    // Cache the assistant ID
    assistantCache[cacheKey] = assistant?.id;
    console.log(`✅ Created new assistant (${model}): ${assistant?.id}`);
    
    return assistant?.id;
  } catch (error) {
    console.error(`❌ Error creating assistant with ${model}:`, error);
    throw new Error(`Falha ao criar assistente: ${error.message}`);
  }
}

/**
 * Upload file to OpenAI for use with Assistants API
 * @param {File} file - File object to upload
 * @returns {Promise<string>} File ID from OpenAI
 */
async function uploadFileToOpenAI(file) {
  try {
    console.log(`📤 Uploading file: ${file?.name} (${(file?.size / 1024)?.toFixed(2)} KB)`);
    
    const uploadedFile = await openai?.files?.create({
      file: file,
      purpose: 'assistants',
    });

    console.log(`✅ File uploaded successfully: ${uploadedFile?.id}`);
    return uploadedFile?.id;
  } catch (error) {
    console.error('❌ Error uploading file:', error);
    
    // Retry logic for network errors
    if (error?.code === 'ECONNRESET' || error?.code === 'ETIMEDOUT') {
      console.log('🔄 Retrying file upload...');
      try {
        const uploadedFile = await openai?.files?.create({
          file: file,
          purpose: 'assistants',
        });
        console.log(`✅ File uploaded successfully on retry: ${uploadedFile?.id}`);
        return uploadedFile?.id;
      } catch (retryError) {
        throw new Error(`Falha ao enviar arquivo após tentativa de retry: ${retryError.message}`);
      }
    }
    
    throw new Error(`Falha ao enviar arquivo: ${error.message}`);
  }
}

/**
 * Create or retrieve a thread for conversation management
 * @param {string} conversationId - Unique conversation identifier
 * @param {Array<string>} fileIds - Optional array of file IDs to attach
 * @returns {Promise<string>} Thread ID
 */
async function getOrCreateThread(conversationId = null, fileIds = []) {
  // Check cache for existing thread
  if (conversationId && threadCache?.has(conversationId)) {
    const cachedThreadId = threadCache?.get(conversationId);
    console.log(`✅ Using cached thread: ${cachedThreadId}`);
    return cachedThreadId;
  }

  try {
    // Create new thread with optional file attachments
    const threadParams = {};
    
    if (fileIds && fileIds?.length > 0) {
      threadParams.tool_resources = {
        file_search: {
          vector_store_ids: [] // OpenAI will auto-create vector store from files
        }
      };
      
      // Attach files as messages
      threadParams.messages = fileIds?.map(fileId => ({
        role: 'user',
        content: 'Documento anexado para análise.',
        attachments: [{ file_id: fileId, tools: [{ type: 'file_search' }] }]
      }));
    }

    const thread = await openai?.beta?.threads?.create(threadParams);
    
    // Cache thread ID if conversation ID provided
    if (conversationId) {
      threadCache?.set(conversationId, thread?.id);
    }
    
    console.log(`✅ Created new thread: ${thread?.id}`);
    return thread?.id;
  } catch (error) {
    console.error('❌ Error creating thread:', error);
    throw new Error(`Falha ao criar thread: ${error.message}`);
  }
}

/**
 * Add message to existing thread
 * @param {string} threadId - Thread ID
 * @param {string} content - Message content
 * @param {Array<string>} fileIds - Optional file IDs to attach
 * @returns {Promise<void>}
 */
async function addMessageToThread(threadId, content, fileIds = []) {
  try {
    const messageParams = {
      role: 'user',
      content: content,
    };

    // Attach files if provided
    if (fileIds && fileIds?.length > 0) {
      messageParams.attachments = fileIds?.map(fileId => ({
        file_id: fileId,
        tools: [{ type: 'file_search' }]
      }));
    }

    await openai?.beta?.threads?.messages?.create(threadId, messageParams);
    console.log(`✅ Message added to thread: ${threadId}`);
  } catch (error) {
    console.error('❌ Error adding message to thread:', error);
    throw new Error(`Falha ao adicionar mensagem: ${error.message}`);
  }
}

/**
 * Run assistant on thread and stream response
 * @param {string} threadId - Thread ID
 * @param {string} assistantId - Assistant ID
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal
 * @param {number} maxTokens - ✅ NEW: Maximum tokens for response (default 3000)
 * @returns {Promise<{content: string, model: string, tokensUsed: number}>}
 */
async function runAssistantWithStreaming(
  threadId,
  assistantId,
  onChunk,
  onProgress,
  signal = null,
  maxTokens = TOKEN_LIMITS?.DEFAULT_TOKENS // ✅ NEW: Token limit parameter
) {
  try {
    onProgress?.({ status: '🔍 Lendo arquivo...', phase: 'reading' });

    let fullResponse = '';
    let runStatus = 'queued';
    let hasStartedProcessing = false;
    let tokensUsed = 0; // ✅ NEW: Track token usage

    // ✅ NEW: Validate and clamp token limit
    const clampedMaxTokens = Math.max(
      TOKEN_LIMITS?.MIN_TOKENS,
      Math.min(maxTokens, TOKEN_LIMITS?.MAX_TOKENS)
    );

    console.log(`🎯 Using token limit: ${clampedMaxTokens} (requested: ${maxTokens})`);

    // Create run with streaming and token limit
    const stream = await openai?.beta?.threads?.runs?.create(
      threadId,
      {
        assistant_id: assistantId,
        stream: true,
        max_completion_tokens: clampedMaxTokens, // ✅ NEW: Apply token limit
      }
    );

    // Process stream events
    for await (const event of stream) {
      // Check for cancellation
      if (signal?.aborted) {
        throw new Error('Análise cancelada pelo usuário');
      }

      // Handle different event types
      if (event?.event === 'thread.run.in_progress' && !hasStartedProcessing) {
        hasStartedProcessing = true;
        onProgress?.({ status: '📝 Gerando resumo...', phase: 'generating' });
      } else if (event?.event === 'thread.run.requires_action') {
        runStatus = 'requires_action';
        onProgress?.({ status: '⏳ Processando informações...', phase: 'requires_action' });
      } else if (event?.event === 'thread.run.completed') {
        runStatus = 'completed';
        
        // ✅ NEW: Extract token usage from completed event
        if (event?.data?.usage) {
          tokensUsed = event?.data?.usage?.total_tokens || 0;
          console.log(`📊 Tokens utilizados: ${tokensUsed}`);
        }
        
        onProgress?.({ status: '✅ Resumo concluído', phase: 'completed' });
      } else if (event?.event === 'thread.run.failed') {
        runStatus = 'failed';
        const errorMessage = event?.data?.last_error?.message || 'Erro desconhecido';
        throw new Error(`Falha na análise: ${errorMessage}`);
      } else if (event?.event === 'thread.message.delta') {
        // Stream message content
        const delta = event?.data?.delta;
        if (delta?.content) {
          for (const contentBlock of delta?.content) {
            if (contentBlock?.type === 'text' && contentBlock?.text?.value) {
              const chunk = contentBlock?.text?.value;
              fullResponse += chunk;
              onChunk?.(chunk);
            }
          }
        }
      } else if (event?.event === 'thread.message.completed') {
        console.log('✅ Message completed');
      }
    }

    if (runStatus !== 'completed') {
      throw new Error('Assistente não completou a análise corretamente');
    }

    // If no response was streamed, retrieve messages manually
    if (!fullResponse) {
      onProgress?.({ status: '📥 Recuperando resposta...', phase: 'retrieving' });
      
      const messages = await openai?.beta?.threads?.messages?.list(threadId, {
        order: 'desc',
        limit: 1
      });

      if (messages?.data?.length > 0) {
        const lastMessage = messages?.data?.[0];
        if (lastMessage?.role === 'assistant') {
          for (const content of lastMessage?.content) {
            if (content?.type === 'text') {
              fullResponse += content?.text?.value;
            }
          }
        }
      }
    }

    return {
      content: fullResponse,
      model: assistantId?.includes('4o-mini') ? PRIMARY_MODEL : FALLBACK_MODEL,
      tokensUsed: tokensUsed // ✅ NEW: Return token count
    };

  } catch (error) {
    console.error('❌ Error running assistant:', error);
    
    // Enhanced error handling with timeout detection
    if (error?.code === 'ETIMEDOUT' || error?.message?.includes('timeout')) {
      throw new Error('⏱️ Tempo limite excedido. O documento pode ser muito grande. Tente novamente ou divida o arquivo.');
    }
    
    throw error;
  }
}

/**
 * Main function: Analyze documents using OpenAI Assistants API
 * @param {Array<File>} files - Array of File objects to analyze
 * @param {string} userMessage - User's question or instruction
 * @param {string} conversationId - Optional conversation ID for thread reuse
 * @param {Function} onChunk - Callback for streaming response chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {number} maxTokens - ✅ NEW: Maximum tokens for response (default 3000, range 2000-4000)
 * @returns {Promise<{content: string, model: string, filesProcessed: number, tokensUsed: number, consolidatedSummary: boolean}>}
 */
export async function analyzeDocumentsWithAssistant(
  files = [],
  userMessage = '',
  conversationId = null,
  onChunk,
  onProgress,
  signal = null,
  maxTokens = TOKEN_LIMITS?.DEFAULT_TOKENS // ✅ NEW: Token limit parameter
) {
  let currentModel = PRIMARY_MODEL;
  let uploadedFileIds = [];
  const MAX_RETRIES = 2;
  let retryCount = 0;

  try {
    // Validate inputs
    if (!userMessage && files?.length === 0) {
      throw new Error('Forneça pelo menos uma mensagem ou um documento para análise');
    }

    // ✅ NEW: Enhanced prompt for multiple files consolidation
    const isMultipleFiles = files?.length > 1;
    const consolidationPrompt = isMultipleFiles 
      ? `\n\nIMPORTANTE: Foram enviados ${files?.length} arquivos. Analise todos e forneça um ÚNICO resumo consolidado organizado por capítulos/tópicos principais, identificando temas comuns entre os documentos.`
      : '';

    const finalUserMessage = userMessage 
      ? `${userMessage}${consolidationPrompt}`
      : `Analise este(s) documento(s) e forneça um resumo detalhado${isMultipleFiles ? ' consolidado' : ''}.${consolidationPrompt}`;

    // Step 1: Upload files if provided (with detailed progress)
    if (files?.length > 0) {
      for (let i = 0; i < files?.length; i++) {
        const file = files?.[i];
        const fileNumber = files?.length > 1 ? ` (${i + 1}/${files?.length})` : '';
        onProgress?.({ 
          status: `📤 Enviando arquivo${fileNumber}...`, 
          phase: 'uploading',
          fileName: file?.name
        });
        
        let uploadSuccess = false;
        let uploadAttempts = 0;
        
        while (!uploadSuccess && uploadAttempts < MAX_RETRIES) {
          try {
            const fileId = await uploadFileToOpenAI(file);
            uploadedFileIds?.push(fileId);
            uploadSuccess = true;
          } catch (uploadError) {
            uploadAttempts++;
            if (uploadAttempts < MAX_RETRIES) {
              onProgress?.({ 
                status: `🔄 Tentando reenviar arquivo... (tentativa ${uploadAttempts + 1}/${MAX_RETRIES})`, 
                phase: 'retrying',
                fileName: file?.name
              });
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
            } else {
              throw new Error(`Falha ao enviar "${file?.name}" após ${MAX_RETRIES} tentativas: ${uploadError.message}`);
            }
          }
        }
      }
      
      onProgress?.({ 
        status: `✅ ${files?.length} arquivo(s) enviado(s) com sucesso`, 
        phase: 'uploaded' 
      });
    }

    // Step 2: Get or create assistant (try primary model first)
    let assistantId;
    try {
      onProgress?.({ status: '🤖 Preparando assistente...', phase: 'assistant_setup' });
      assistantId = await getOrCreateAssistant(PRIMARY_MODEL);
      currentModel = PRIMARY_MODEL;
    } catch (primaryError) {
      console.warn('⚠️ Primary model failed, trying fallback model...');
      onProgress?.({ status: '🔄 Alternando para modelo alternativo...', phase: 'fallback' });
      assistantId = await getOrCreateAssistant(FALLBACK_MODEL);
      currentModel = FALLBACK_MODEL;
    }

    // Step 3: Create or retrieve thread
    onProgress?.({ status: '🔗 Configurando conversa...', phase: 'thread_setup' });
    let threadId = await getOrCreateThread(conversationId, uploadedFileIds);

    // Step 4: Add user message to thread
    onProgress?.({ status: '💬 Enviando pergunta...', phase: 'adding_message' });
    await addMessageToThread(threadId, finalUserMessage); // ✅ Use enhanced prompt

    // Step 5: Run assistant and stream response (with token limit)
    const result = await runAssistantWithStreaming(
      threadId,
      assistantId,
      onChunk,
      onProgress,
      signal,
      maxTokens // ✅ NEW: Pass token limit
    );

    return {
      content: result?.content,
      model: currentModel,
      filesProcessed: files?.length,
      threadId: threadId,
      tokensUsed: result?.tokensUsed, // ✅ NEW: Return token usage
      consolidatedSummary: isMultipleFiles // ✅ NEW: Flag for consolidated summaries
    };

  } catch (error) {
    // Handle specific errors with user-friendly messages
    if (error?.message === 'Análise cancelada pelo usuário') {
      throw error;
    }

    console.error('❌ Error in document analysis:', error);
    
    if (error?.response?.status === 401) {
      throw new Error('🔐 Erro de autenticação. Verifique sua chave da API OpenAI.');
    } else if (error?.response?.status === 429) {
      throw new Error('⏸️ Limite de requisições atingido. Aguarde alguns instantes e tente novamente.');
    } else if (error?.response?.status === 400) {
      throw new Error('⚠️ Erro de validação. Verifique o formato dos arquivos (PDF, DOCX, DOC, TXT aceitos).');
    } else if (error?.response?.status === 413) {
      throw new Error('📦 Arquivo muito grande. O tamanho máximo permitido é 512MB.');
    } else if (error?.code === 'ETIMEDOUT' || error?.message?.includes('timeout')) {
      throw new Error('⏱️ Tempo limite excedido. O processamento pode estar demorando mais que o esperado. Tente novamente.');
    }
    
    throw new Error(`❌ Falha na análise: ${error.message}`);
  } finally {
    // Optional: Cleanup uploaded files
    // Uncomment if you want to delete files immediately after analysis
    // for (const fileId of uploadedFileIds) {
    //   try {
    //     await openai.files.delete(fileId);
    //   } catch (e) {
    //     console.warn(`Could not delete file ${fileId}:`, e);
    //   }
    // }
  }
}

/**
 * 🆕 HYBRID APPROACH: Analyze documents using two-step process
 * Step 1: gpt-4o-mini reads and indexes content
 * Step 2: gpt-4o generates final response using extracted data
 * @param {Array<File>} files - Array of File objects to analyze
 * @param {string} userPrompt - User's question or instruction
 * @param {string} conversationId - Conversation identifier for thread management
 * @param {Function} onChunk - Callback for streaming response chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{content: string, model: string, tokensUsed: number}>}
 */
export async function analyzeDocumentsWithHybridApproach(
  files,
  userPrompt,
  conversationId = null,
  onChunk,
  onProgress,
  signal = null
) {
  let uploadedFileIds = [];
  let indexingAssistantId = null;
  let responseAssistantId = null;
  let threadId = null;
  let extractedContent = '';

  try {
    // ========== STEP 1: UPLOAD FILES ==========
    onProgress?.({ status: '📤 Enviando arquivos...', phase: 'uploading', step: 1, totalSteps: 4 });
    
    uploadedFileIds = await Promise.all(
      files?.map(file => uploadFileToOpenAI(file))
    );

    console.log(`✅ ${uploadedFileIds?.length} arquivo(s) enviado(s)`);

    // ========== STEP 2: INDEXING WITH gpt-4o-mini ==========
    onProgress?.({ status: '🔍 Lendo e indexando conteúdo (gpt-4o-mini)...', phase: 'indexing', step: 2, totalSteps: 4 });
    
    // Create indexing assistant with gpt-4o-mini
    indexingAssistantId = await getOrCreateAssistant(INDEXING_MODEL, false);
    
    // Create thread with files
    threadId = await getOrCreateThread(conversationId, uploadedFileIds);
    
    // Add indexing instruction
    const indexingPrompt = `Extraia e organize todo o conteúdo relevante deste(s) documento(s). Estruture as informações de forma clara e completa, mantendo todos os detalhes importantes. Não resuma ainda - apenas organize o conteúdo extraído.`;
    
    await addMessageToThread(threadId, indexingPrompt, []);
    
    // Run indexing assistant and capture extracted content
    const indexingResult = await runAssistantWithStreaming(
      threadId,
      indexingAssistantId,
      (chunk) => {
        extractedContent += chunk;
      },
      (progressInfo) => {
        onProgress?.({
          status: `🔍 Indexando: ${progressInfo?.status}`,
          phase: 'indexing',
          step: 2,
          totalSteps: 4
        });
      },
      signal,
      4000 // Max tokens for indexing
    );

    console.log(`✅ Conteúdo indexado: ${extractedContent?.length} caracteres`);

    // ========== STEP 3: GENERATE RESPONSE WITH gpt-4o ==========
    onProgress?.({ status: '✨ Gerando resposta final (gpt-4o)...', phase: 'generating', step: 3, totalSteps: 4 });
    
    // Create response assistant with gpt-4o
    responseAssistantId = await getOrCreateAssistant(RESPONSE_MODEL, false);
    
    // Create new thread for final response (clean context)
    const responseThreadId = await getOrCreateThread(null, []);
    
    // Combine extracted content with user prompt
    const finalPrompt = `Com base no conteúdo extraído abaixo, ${userPrompt}

=== CONTEÚDO EXTRAÍDO ===
${extractedContent}
=== FIM DO CONTEÚDO ===

Agora forneça uma resposta completa e bem estruturada.`;
    
    await addMessageToThread(responseThreadId, finalPrompt, []);
    
    // Run response assistant with streaming
    let finalResponse = '';
    const responseResult = await runAssistantWithStreaming(
      responseThreadId,
      responseAssistantId,
      (chunk) => {
        finalResponse += chunk;
        onChunk?.(chunk); // Stream to user
      },
      (progressInfo) => {
        onProgress?.({
          status: `✨ Gerando: ${progressInfo?.status}`,
          phase: 'generating',
          step: 3,
          totalSteps: 4
        });
      },
      signal,
      4000 // Max tokens for response
    );

    // ========== STEP 4: COMPLETE ==========
    onProgress?.({ status: '✅ Análise concluída', phase: 'completed', step: 4, totalSteps: 4 });

    console.log(`✅ Resposta final gerada: ${finalResponse?.length} caracteres`);
    console.log(`📊 Processo híbrido: ${INDEXING_MODEL} (indexação) → ${RESPONSE_MODEL} (resposta)`);

    return {
      content: finalResponse,
      model: `${INDEXING_MODEL} → ${RESPONSE_MODEL}`,
      tokensUsed: (indexingResult?.tokensUsed || 0) + (responseResult?.tokensUsed || 0),
      indexedContent: extractedContent
    };

  } catch (error) {
    console.error('❌ Error in hybrid document analysis:', error);
    
    // Fallback to single-model approach if hybrid fails
    if (error?.message?.includes('gpt-4o')) {
      console.log('⚠️ Fallback: Tentando análise com modelo único...');
      return await analyzeDocumentsWithAssistant(
        files,
        userPrompt,
        conversationId,
        onChunk,
        onProgress,
        signal
      );
    }
    
    throw error;
  }
}

/**
 * Retrieve thread messages for conversation history
 * @param {string} threadId - Thread ID
 * @param {number} limit - Maximum number of messages to retrieve
 * @returns {Promise<Array>} Array of message objects
 */
export async function getThreadMessages(threadId, limit = 20) {
  try {
    const messages = await openai?.beta?.threads?.messages?.list(threadId, {
      order: 'asc',
      limit: limit
    });

    return messages?.data?.map(msg => ({
      role: msg?.role,
      content: msg?.content?.[0]?.text?.value || '',
      timestamp: new Date(msg.created_at * 1000),
      id: msg?.id
    }));
  } catch (error) {
    console.error('❌ Error retrieving thread messages:', error);
    throw new Error(`Falha ao recuperar mensagens: ${error.message}`);
  }
}

/**
 * Delete thread (cleanup)
 * @param {string} threadId - Thread ID to delete
 * @returns {Promise<boolean>}
 */
export async function deleteThread(threadId) {
  try {
    await openai?.beta?.threads?.del(threadId);
    
    // Remove from cache
    for (const [conversationId, cachedThreadId] of threadCache?.entries()) {
      if (cachedThreadId === threadId) {
        threadCache?.delete(conversationId);
        break;
      }
    }
    
    console.log(`✅ Thread deleted: ${threadId}`);
    return true;
  } catch (error) {
    console.error('❌ Error deleting thread:', error);
    return false;
  }
}

/**
 * Clear all cached assistants and threads
 */
export function clearAssistantCache() {
  assistantCache.primary = null;
  assistantCache.fallback = null;
  threadCache?.clear();
  console.log('✅ Assistant cache cleared');
}

/**
 * Check if Assistants API is available and working
 * @returns {Promise<{available: boolean, error?: string}>}
 */
export async function checkAssistantsAPIStatus() {
  try {
    // Try to list assistants (lightweight check)
    await openai?.beta?.assistants?.list({ limit: 1 });
    return { available: true };
  } catch (error) {
    console.error('❌ Assistants API check failed:', error);
    return { 
      available: false, 
      error: error?.message 
    };
  }
}

// ✅ NEW: LocalStorage key for document analysis history
const DOCUMENT_HISTORY_KEY = 'ai_document_analysis_history';

/**
 * ✅ NEW: Save document analysis to localStorage
 * @param {Object} analysisData - Analysis result data
 * @returns {void}
 */
export function saveDocumentAnalysisToHistory(analysisData) {
  try {
    const history = getDocumentAnalysisHistory();
    
    const newEntry = {
      id: Date.now()?.toString(),
      timestamp: new Date()?.toISOString(),
      files: analysisData?.files || [],
      summary: analysisData?.summary || '',
      tokensUsed: analysisData?.tokensUsed || 0,
      model: analysisData?.model || '',
      consolidatedSummary: analysisData?.consolidatedSummary || false
    };
    
    // Add to beginning (most recent first)
    history?.unshift(newEntry);
    
    // Keep only last 50 entries to prevent localStorage bloat
    const trimmedHistory = history?.slice(0, 50);
    
    localStorage.setItem(DOCUMENT_HISTORY_KEY, JSON.stringify(trimmedHistory));
    console.log('✅ Document analysis saved to history');
  } catch (error) {
    console.error('❌ Error saving document analysis to history:', error);
  }
}

/**
 * ✅ NEW: Get document analysis history from localStorage
 * @returns {Array} Array of analysis history entries
 */
export function getDocumentAnalysisHistory() {
  try {
    const historyJSON = localStorage.getItem(DOCUMENT_HISTORY_KEY);
    return historyJSON ? JSON.parse(historyJSON) : [];
  } catch (error) {
    console.error('❌ Error retrieving document analysis history:', error);
    return [];
  }
}

/**
 * ✅ NEW: Clear document analysis history
 * @returns {void}
 */
export function clearDocumentAnalysisHistory() {
  try {
    localStorage.removeItem(DOCUMENT_HISTORY_KEY);
    console.log('✅ Document analysis history cleared');
  } catch (error) {
    console.error('❌ Error clearing document analysis history:', error);
  }
}

/**
 * ✅ NEW: Delete specific document analysis from history
 * @param {string} analysisId - ID of analysis to delete
 * @returns {boolean} Success status
 */
export function deleteDocumentAnalysis(analysisId) {
  try {
    const history = getDocumentAnalysisHistory();
    const updatedHistory = history?.filter(entry => entry?.id !== analysisId);
    localStorage.setItem(DOCUMENT_HISTORY_KEY, JSON.stringify(updatedHistory));
    console.log(`✅ Document analysis ${analysisId} deleted`);
    return true;
  } catch (error) {
    console.error('❌ Error deleting document analysis:', error);
    return false;
  }
}